import _ from 'lodash';
import type { ColDef, ColGroupDef } from 'ag-grid-community';

import {
  buildApiShim,
  buildColumnShim,
  buildNodeShim,
  TableBridge,
} from './agParamsShim';

type AnyFn = (...args: any[]) => any;
type AgColumnDef = ColDef | ColGroupDef;

// A converted column. Shaped as a TanStack column def (id, header,
// accessorFn, columns, sort options, meta) but kept independent of the
// table's feature registry, so the same columns feed both the React table and
// the headless CSV builder.
export interface DataTableColumn {
  id: string;
  header: string;
  accessorFn?: (row: DataTable.Row, index: number) => unknown;
  columns?: DataTableColumn[];
  enableSorting?: boolean;
  sortDescFirst?: boolean;
  sortUndefined?: false;
  sortFn?: (rowA: SortableRow, rowB: SortableRow, columnId: string) => number;
  meta: DataTable.ColumnMeta;
}

interface SortableRow {
  id: string;
  index: number;
  original: DataTable.Row;
  getValue: <T>(columnId: string) => T;
}

export const isGroupDef = (def: AgColumnDef): def is ColGroupDef =>
  'children' in def && Array.isArray(def.children);

const DEFAULT_SORTING_ORDER: DataTable.SortDirection[] = ['asc', 'desc', null];

// AG's colDef/defaultColDef merge is deep for plain objects
// (cellRendererParams, headerComponentParams) and by reference for
// everything else (functions, arrays of metrics...)
const mergeColDef = ({
  defaultColDef,
  colDef,
}: {
  defaultColDef: ColDef;
  colDef: ColDef;
}): ColDef =>
  _.mergeWith({}, defaultColDef, colDef, (_objValue, srcValue) =>
    _.isPlainObject(srcValue) ? undefined : srcValue,
  );

const getField = (row: DataTable.Row | undefined, field: string) => {
  if (!row) {
    return undefined;
  }
  // AG reads dotted fields as paths, as long as the row doesn't own the
  // literal key
  if (field.includes('.') && !(field in row)) {
    return _.get(row, field);
  }
  return row[field];
};

// AG's default comparator: nullish values first, then natural order
export const agDefaultComparator = (a: unknown, b: unknown) => {
  const isANullish = a === null || a === undefined;
  const isBNullish = b === null || b === undefined;
  if (isANullish && isBNullish) {
    return 0;
  }
  if (isANullish) {
    return -1;
  }
  if (isBNullish) {
    return 1;
  }
  if (typeof a === 'string' && typeof b === 'string') {
    return a < b ? -1 : a > b ? 1 : 0;
  }
  // @ts-ignore: AG compares whatever it's given with < and >
  return a < b ? -1 : a > b ? 1 : 0;
};

const toClassName = (
  value: unknown,
  params: { [key: string]: any },
): string | undefined => {
  if (!value) {
    return undefined;
  }
  if (typeof value === 'function') {
    const fn: AnyFn = value as AnyFn;
    return toClassName(fn(params), params);
  }
  if (Array.isArray(value)) {
    return value.join(' ');
  }
  return typeof value === 'string' ? value : undefined;
};

// AG puts left-pinned columns first and right-pinned columns last, whatever
// their position in the defs
const orderByPinning = (defs: AgColumnDef[]) => {
  const pinOf = (def: AgColumnDef) =>
    isGroupDef(def)
      ? undefined
      : def.pinned === true || def.pinned === 'left'
        ? 'left'
        : def.pinned === 'right'
          ? 'right'
          : undefined;
  return [
    ...defs.filter((d) => pinOf(d) === 'left'),
    ...defs.filter((d) => pinOf(d) === undefined),
    ...defs.filter((d) => pinOf(d) === 'right'),
  ];
};

const resolveComponent = ({
  component,
  components,
}: {
  component: unknown;
  components: { [key: string]: React.ComponentType<any> };
}): React.ComponentType<any> | undefined => {
  if (!component) {
    return undefined;
  }
  if (typeof component === 'string') {
    return components[component];
  }
  if (typeof component === 'function' || typeof component === 'object') {
    // A component reference (function, class or memo/forwardRef object)
    const resolved: React.ComponentType<any> =
      component as React.ComponentType<any>;
    return resolved;
  }
  return undefined;
};

const buildLeaf = ({
  userColDef,
  defaultColDef,
  components,
  bridge,
  fallbackId,
}: {
  userColDef: ColDef;
  defaultColDef: ColDef;
  components: { [key: string]: React.ComponentType<any> };
  bridge: TableBridge;
  fallbackId: string;
}): DataTableColumn => {
  const colDef = mergeColDef({ defaultColDef, colDef: userColDef });
  const id = colDef.colId ?? colDef.field ?? fallbackId;
  const column = buildColumnShim({ bridge, colId: id, colDef, userColDef });
  const api = buildApiShim(bridge);

  const valueGetter = colDef.valueGetter;
  const accessorFn = (row: DataTable.Row, rowIndex: number) => {
    if (typeof valueGetter === 'function') {
      const getter: AnyFn = valueGetter;
      return getter({
        data: row,
        node: buildNodeShim({
          bridge,
          rowId: `${rowIndex}`,
          data: row,
          rowIndex,
          isPinned: false,
        }),
        colDef,
        column,
        api,
        context: bridge.context,
        getValue: (field: string) => getField(row, field),
      });
    }
    if (colDef.field) {
      return getField(row, colDef.field);
    }
    return undefined;
  };

  // AG also accepts { type, direction } sort definitions; we only use the
  // direction
  const sortingOrder: DataTable.SortDirection[] = (
    colDef.sortingOrder ?? DEFAULT_SORTING_ORDER
  ).map((s) => (s && typeof s === 'object' ? s.direction : s));
  const comparator: AnyFn | undefined =
    typeof colDef.comparator === 'function' ? colDef.comparator : undefined;
  const sortFn = (rowA: SortableRow, rowB: SortableRow, columnId: string) => {
    const a = rowA.getValue(columnId);
    const b = rowB.getValue(columnId);
    if (!comparator) {
      return agDefaultComparator(a, b);
    }
    const toNode = (row: SortableRow) =>
      buildNodeShim({
        bridge,
        rowId: row.id,
        data: row.original,
        rowIndex: row.index,
        isPinned: false,
      });
    return comparator(a, b, toNode(rowA), toNode(rowB), false);
  };

  const valueFormatter: AnyFn | undefined =
    typeof colDef.valueFormatter === 'function'
      ? colDef.valueFormatter
      : undefined;
  const headerClass = toClassName(colDef.headerClass, { colDef, column });
  // A cell editor (AG's editable + cellEditor). Ours are all popups
  // (cellEditorPopup); one without it still opens over the cell
  const editorComponent = colDef.editable
    ? resolveComponent({ component: colDef.cellEditor, components })
    : undefined;
  const editable = colDef.editable;
  const tooltipValueGetter: AnyFn | undefined =
    typeof colDef.tooltipValueGetter === 'function'
      ? colDef.tooltipValueGetter
      : undefined;
  const editor: DataTable.ColumnEditor | undefined = editorComponent
    ? {
        component: editorComponent,
        params: colDef.cellEditorParams ?? {},
        isEditable: (params) => {
          if (typeof editable === 'function') {
            const fn: AnyFn = editable;
            return !!fn({ ...params, colDef, column, api });
          }
          return !!editable;
        },
      }
    : undefined;
  const typeList = Array.isArray(colDef.type)
    ? colDef.type
    : colDef.type
      ? [colDef.type]
      : [];

  return {
    id,
    header: colDef.headerName ?? _.startCase(colDef.field ?? ''),
    accessorFn,
    enableSorting: colDef.sortable !== false,
    sortDescFirst: sortingOrder[0] === 'desc',
    // Our comparators place nullish values themselves, as AG does
    sortUndefined: false,
    sortFn,
    meta: {
      colDef,
      userColDef,
      cellComponent: resolveComponent({
        component: colDef.cellRenderer,
        components,
      }),
      headerComponent: resolveComponent({
        component: colDef.headerComponent,
        components,
      }),
      cellRendererParams: colDef.cellRendererParams ?? {},
      headerComponentParams: colDef.headerComponentParams ?? {},
      isHidden: colDef.hide === true,
      isRightAligned:
        (headerClass ?? '').includes('rightAlignedHeader') ||
        typeList.includes('numericColumn') ||
        typeList.includes('rightAligned'),
      pinned:
        colDef.pinned === true || colDef.pinned === 'left'
          ? 'left'
          : colDef.pinned === 'right'
            ? 'right'
            : undefined,
      isMovable: !colDef.suppressMovable,
      flex: colDef.flex ?? undefined,
      width: colDef.width,
      minWidth: colDef.minWidth,
      maxWidth: colDef.maxWidth,
      editor,
      tooltipValueGetter: tooltipValueGetter
        ? (value, data) => {
            const tooltip = tooltipValueGetter({
              value,
              data,
              colDef,
              column,
              api,
              context: bridge.context,
              node: null,
              location: 'cell',
            });
            return tooltip === undefined || tooltip === null
              ? undefined
              : `${tooltip}`;
          }
        : undefined,
      cellClass: typeof colDef.cellClass === 'string' ? colDef.cellClass : '',
      headerClass,
      headerTooltip: colDef.headerTooltip,
      sortingOrder,
      valueFormatter: valueFormatter
        ? (value, data) =>
            valueFormatter({
              value,
              data,
              colDef,
              column,
              api,
              context: bridge.context,
              node: null,
            })
        : undefined,
    },
  };
};

const convert = ({
  defs,
  defaultColDef,
  components,
  bridge,
  path,
}: {
  defs: AgColumnDef[];
  defaultColDef: ColDef;
  components: { [key: string]: React.ComponentType<any> };
  bridge: TableBridge;
  path: string;
}): DataTableColumn[] =>
  defs.map((def, index) => {
    if (isGroupDef(def)) {
      const groupId = def.groupId ?? `group_${path}${index}`;
      const children = convert({
        defs: def.children,
        defaultColDef,
        components,
        bridge,
        path: `${path}${index}_`,
      });
      return {
        id: groupId,
        header: def.headerName ?? '',
        columns: children,
        meta: {
          isMovable: children.every((child) => child.meta.isMovable),
          colDef: def,
          userColDef: def,
          cellRendererParams: {},
          headerComponentParams: def.headerGroupComponentParams ?? {},
          headerComponent: resolveComponent({
            component: def.headerGroupComponent,
            components,
          }),
          isRightAligned: false,
          headerClass: toClassName(def.headerClass, {}),
          headerTooltip: def.headerTooltip,
          sortingOrder: DEFAULT_SORTING_ORDER,
        },
      };
    }
    return buildLeaf({
      userColDef: def,
      defaultColDef,
      components,
      bridge,
      fallbackId: `${path}${index}`,
    });
  });

const collectInitialSorting = (defs: AgColumnDef[]) => {
  const sorted: { id: string; desc: boolean; sortIndex: number }[] = [];
  const visit = (list: AgColumnDef[]) =>
    list.forEach((def) => {
      if (isGroupDef(def)) {
        visit(def.children);
        return;
      }
      const sort = def.sort ?? def.initialSort;
      const id = def.colId ?? def.field;
      if (sort && id) {
        sorted.push({
          id,
          desc: sort === 'desc',
          sortIndex: def.sortIndex ?? def.initialSortIndex ?? sorted.length,
        });
      }
    });
  visit(defs);
  return _.sortBy(sorted, 'sortIndex').map(({ id, desc }) => ({ id, desc }));
};

const fillFallbackIds = ({
  defs,
  path,
}: {
  defs: AgColumnDef[];
  path: string;
}): AgColumnDef[] =>
  defs.map((def, index) => {
    if (isGroupDef(def)) {
      return {
        ...def,
        groupId: def.groupId ?? `group_${path}${index}`,
        children: fillFallbackIds({
          defs: def.children,
          path: `${path}${index}_`,
        }),
      };
    }
    return def.colId ?? def.field ? def : { ...def, colId: `${path}${index}` };
  });

// Columns (and groups) without an id of their own given the one convert
// would fall back to (their place in the defs), from the defs as written, so
// they keep it when the user moves columns about
export const withFallbackIds = (columnDefs: AgColumnDef[]): AgColumnDef[] =>
  fillFallbackIds({ defs: columnDefs, path: '' });

// The migration lever: the AG column definitions our builders already
// produce (buildMatrixColDefs, useColumnDefs...) as DataTable columns, with
// the AG renderers resolved from the same component registry AG uses.
const fromAgColDefs = ({
  columnDefs,
  defaultColDef = {},
  components = {},
  bridge,
}: {
  columnDefs: AgColumnDef[];
  defaultColDef?: ColDef;
  components?: { [key: string]: React.ComponentType<any> };
  bridge: TableBridge;
}): {
  columns: DataTableColumn[];
  initialSorting: { id: string; desc: boolean }[];
} => ({
  columns: convert({
    defs: orderByPinning(columnDefs),
    defaultColDef,
    components,
    bridge,
    path: '',
  }),
  initialSorting: collectInitialSorting(columnDefs),
});

export default fromAgColDefs;
